import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

/**
 * Job linkage data derived from a gallery's existing images.
 *
 * When images are synced into galleries, they carry the Jobber job_id
 * and franchise_region from the franchise_image_jobs table. This hook
 * queries the first linked image to get that info for syncing new images.
 *
 * @see useSyncGalleryImages - Uses this data to call the Worker endpoint
 * @see /apps/production/jobber-image-processor - Worker that processes syncs
 */
interface GalleryJobLinkage {
  job_id: string        // Base64-encoded Jobber GraphQL ID (e.g., "Z2lkOi8vSm9iYmVyL0pvYi8xMjQxMDAxMjE=")
  franchise_region: string  // "HQ", "north_york", etc.
  job_number: number | null
}

/**
 * Fetches job linkage info from a gallery's existing images.
 *
 * Instead of requiring gallery_jobber_details (which only exists post-enrichment),
 * this queries the gallery_all_images view to find the job_id from any linked
 * franchise_image_jobs record.
 */
async function fetchGalleryJobLinkage(galleryId: number): Promise<GalleryJobLinkage | null> {
  // Query gallery_all_images for any job image to get the job_id
  const { data, error } = await supabase
    .from('gallery_all_images')
    .select('job_id, franchise_region, job_number')
    .eq('gallery_id', galleryId)
    .eq('image_type', 'job_image')  // Only job images have the job_id we need
    .not('job_id', 'is', null)
    .limit(1)
    .maybeSingle()

  if (error) {
    console.error('Error fetching gallery job linkage:', error)
    throw error
  }

  if (!data?.job_id || !data?.franchise_region) {
    return null
  }

  return {
    job_id: data.job_id,
    franchise_region: data.franchise_region,
    job_number: data.job_number
  }
}

/**
 * Hook to get job linkage info for syncing new images to a gallery.
 *
 * This enables the "Sync New Images" feature on galleries that have at least
 * one linked job image. The job_id from existing images tells us which Jobber
 * job to query for new photos.
 *
 * @param galleryId - The gallery to check for job linkage
 * @returns Query result with job_id and franchise_region if available
 */
export function useGalleryJobLinkage(galleryId?: number | null) {
  return useQuery<GalleryJobLinkage | null>({
    queryKey: ['gallery-job-linkage', galleryId],
    enabled: !!galleryId,
    queryFn: () => fetchGalleryJobLinkage(galleryId!),
    staleTime: 60_000, // Cache for 1 minute
    retry: 1,
  })
}

export default useGalleryJobLinkage
